import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, OnInit, VERSION, ViewChild, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormGroup, Validators, ReactiveFormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { NgxSpinnerService, NgxSpinnerModule  } from 'ngx-spinner';
import { LocalStorageService } from 'ngx-webstorage';
import { NgxTrimDirectiveModule } from 'ngx-trim-directive';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { TppClaimantService } from '../../../services/tpp-claimant/tpp-claimant.service';
import { TppClaimantsModel } from '../../../models/tppclaimantsmodel';
import { RecaptchaModule, RecaptchaFormsModule } from 'ng-recaptcha-2';
import { requiredIfValidator } from '../../../helpers/check-required.validator';


@Component({
  selector: 'app-tpp-claimants',
  templateUrl: './tpp-claimants.html',
  styleUrls: ['./tpp-claimants.css'],
  imports: [ReactiveFormsModule, CommonModule, RouterLink, RouterLinkActive, NgxTrimDirectiveModule, NgxMaskDirective, NgxSpinnerModule, RecaptchaModule, RecaptchaFormsModule],
  providers: [provideNgxMask()], // Provide at the component level
})
export class TppClaimants implements OnInit {
  @ViewChild('listofFiles') tableRef: ElementRef;
  @ViewChild('fileInput') fileInput: ElementRef;

  tppForm: FormGroup;
  allStates: any = [];
  allCountries: any = [];
  allCompanyHealthTypes: any = [];
  allRelationshipTypes: any = [];
  showDivOptSectionA: boolean = false;
  showDivOptSectionB: boolean = false;
  showDivOtherHealthType: boolean = false;
  showDivOtherRelationshipType: boolean = false;
  submitted = false;
  clicked = false;
  supportingDocuments: File[] = [];
  cmList: any = [];
  cmPAList: any = [];
  submittedClaim: TppClaimantsModel;
  response: string | undefined;
  changed = false;
  dateLoaded: Date;
  claimStartTime: string;
  claimEndTime: string;
  showDivSubmittalErrorMessage: boolean = false;


  validation_ClassMembersErrorMessage = {
    classMemberName: [
      { type: 'required', message: 'This Field is required' },
      { type: "minlength", message: "Field must be at least 2 characters" },
      { type: "maxlength", message: "Field cannot be more than 50 characters" },
      { type: "pattern", message: "Only use letters, numbers, periods, spaces, hyphens, brackets, ampersands, apostophes, parentheses slash(/), pound sign(#)" }
    ],
    classMemberFEIN: [
      { type: 'required', message: 'This Field is required' },
      { type: "minlength", message: "Field must be at least 9 characters" },
      { type: "maxlength", message: "Field cannot be more than 9 characters" },
      { type: "pattern", message: "Only use numbers" }
    ],
  };

  validation_ClassMemberPaidAmountsErrorMessage = {
    classMemberName: [
      { type: 'required', message: 'This Field is required' },
      { type: "minlength", message: "Field must be at least 2 characters" },
      { type: "maxlength", message: "Field cannot be more than 50 characters" },
      { type: "pattern", message: "Only use letters, numbers, periods, spaces, hyphens, brackets, ampersands, apostophes, parentheses slash(/), pound sign(#)" }
    ],
    classMemberPaidAmount: [
      { type: 'required', message: 'This Field is required' },
      { type: "maxlength", message: "Field cannot be more than 15 digits" },
      { type: "pattern", message: "Only use numbers with optional period (.) and up to 4 digits past decimal" }
    ],
  };

  constructor(private fb: FormBuilder,
    private httpClient: HttpClient,
    private storage: LocalStorageService,
    private router: Router,
    private spinner: NgxSpinnerService,
    private tppClaimantService: TppClaimantService) { }


  ngOnInit() {
    this.dateLoaded = new Date();
    this.claimStartTime = this.dateLoaded.toLocaleTimeString();
    this.loadAllStates();
    this.loadAllCompanyHealthTypes();
    this.loadAllRelationshipTypes();
    this.tppForm = this.fb.group({
      optSectionRadio: ['', Validators.compose([Validators.required])],
      insurerOrSelfInsuredName: [''],
      filerType: [''],
      contactName: [''],
      otherNames: [''],
      agentsCompanyName: [''],
      emailAddress: [''],
      street1: [''],
      street2: [''],
      city: [''],
      state: [''],
      companyHealthType: [''],
      relationshipType: [''],
      otherCompanyHealthType: [''],
      otherRelationshipType: [''],
      zip: [''],
      primaryPhone: [''],
      primaryPhoneType: [''],
      tin: [''],
      totalAmountPaid: ['', Validators.compose([Validators.required, Validators.maxLength(12), Validators.pattern(/^\d{1,9}(?:[.]\d{1,2})?$/)])],
      sandoxSettlementClass: [false],
      apotexSettlementClass: [false],
      heritageSettlementClass: [false],
      sunTaroSettlementClass: [false],
      agentsTin: [''],
      signature: ['', Validators.compose([Validators.required, Validators.maxLength(50), Validators.minLength(2), Validators.pattern("^[a-zA-Z0-9 .\\[\\]/#&'()-]+$")])],
      signatureConfirmation: ['', Validators.compose([Validators.required])],
      positionTitle: ['', Validators.compose([Validators.required, Validators.maxLength(50), Validators.minLength(2), Validators.pattern("^[a-zA-Z0-9 .\\[\\]/#&'()-]+$")])],
      googleRecaptchaToken: ['', Validators.compose([Validators.required])],
      classMembers: this.fb.array([]),
    }, {
      validator: (requiredIfValidator('sandoxSettlementClass'))
    });
    this.tppForm.get('primaryPhone')?.valueChanges.subscribe(value => {
      const dropdownControl = this.tppForm.get('primaryPhoneType');
      if (value) {
        dropdownControl?.setValidators([Validators.required]);
      } else {
        dropdownControl?.clearValidators();
      }
      dropdownControl?.updateValueAndValidity();
    });
  }

  getPhoneType2Value() {
    this.changed = true;
    const primaryPhoneType = this.tppForm.get('primaryPhoneType')?.value;

    if (primaryPhoneType !== '') {
      this.tppForm.get('primaryPhone').setValidators(Validators.compose([Validators.required, Validators.maxLength(10), Validators.minLength(10), Validators.pattern("^[0-9]+$")]));
    } else {
      this.tppForm.get('primaryPhone').clearValidators();
    }
    this.tppForm.get('primaryPhone').updateValueAndValidity();
  }

  loadAllStates() {
    this.httpClient.get("assets/States.json").subscribe(data => {
      /* console.log(data);*/
      this.allStates = data;
    })
  }

  loadAllCompanyHealthTypes() {
    this.httpClient.get("assets/CompanyHealthTypes.json").subscribe(data => {
      /* console.log(data);*/
      this.allCompanyHealthTypes = data;
    })
  }

  loadAllRelationshipTypes() {
    this.httpClient.get("assets/RelationshipTypes.json").subscribe(data => {
      /* console.log(data);*/
      this.allRelationshipTypes = data;
    })
  }

  resolved(captchaResponse: string) {
    this.response = captchaResponse;
  }

  get f() { return this.tppForm.controls; }

  getRadioOptSectionRadioValue($event) {
    // if you need the event source and value it can be accessed.
    console.log($event.target.value);
    if ($event.target.value === 'Company') {
      this.showDivOptSectionA = true;
      this.showDivOptSectionB = false;
      this.showDivOtherRelationshipType = false;
      this.tppForm.get('agentsCompanyName').reset();
      this.tppForm.get('agentsCompanyName').clearValidators();
      this.tppForm.get('agentsCompanyName').updateValueAndValidity();
      this.tppForm.get('contactName').reset();
      this.tppForm.get('street1').reset();
      this.tppForm.get('street2').reset();
      this.tppForm.get('city').reset();
      this.tppForm.get('state').reset();
      this.tppForm.get('zip').reset();
      this.tppForm.get('primaryPhone').reset();
      this.tppForm.get('primaryPhoneType').reset();
      this.tppForm.get('agentsTin').reset();
      this.tppForm.get('agentsTin').clearValidators();
      this.tppForm.get('agentsTin').updateValueAndValidity();
      this.tppForm.get('emailAddress').reset();
      this.tppForm.get('relationshipType').reset();
      this.tppForm.get('relationshipType').clearValidators();
      this.tppForm.get('relationshipType').updateValueAndValidity();
      this.tppForm.get('otherRelationshipType').reset();
      this.tppForm.get('otherRelationshipType').clearValidators();
      this.tppForm.get('otherRelationshipType').updateValueAndValidity();

      this.tppForm.get('filerType')?.setValue($event.target.value);
      this.tppForm.get('insurerOrSelfInsuredName').setValidators(Validators.compose([Validators.required, Validators.maxLength(50), Validators.minLength(2), Validators.pattern("^[a-zA-Z0-9 .\\[\\]/#&'()-]+$")]));
      this.tppForm.get('contactName').setValidators(Validators.compose([Validators.maxLength(50), Validators.minLength(2), Validators.pattern("^[a-zA-Z0-9 .\\[\\]/#&'()-]+$")]));
      this.tppForm.get('street1').setValidators(Validators.compose([Validators.required, Validators.maxLength(50), Validators.minLength(2), Validators.pattern("^[a-zA-Z0-9 .\\[\\]/#&'()-]+$")]));
      this.tppForm.get('street2').setValidators(Validators.compose([Validators.maxLength(50), Validators.minLength(2), Validators.pattern("^[a-zA-Z0-9 .\\[\\]/#&'()-]+$")]));
      this.tppForm.get('city').setValidators(Validators.compose([Validators.required, Validators.maxLength(40), Validators.minLength(2), Validators.pattern("^[a-zA-Z0-9 .\\[\\]/#&'()-]+$")]));
      this.tppForm.get('state').setValidators(Validators.compose([Validators.required]));
      this.tppForm.get('zip').setValidators(Validators.compose([Validators.required, Validators.pattern("^[0-9]{5}(?:-[0-9]{4})?$")]));
      this.tppForm.get('primaryPhone').setValidators(Validators.compose([Validators.required, Validators.maxLength(10), Validators.minLength(10), Validators.pattern("^[0-9]+$")]));
      this.tppForm.get('primaryPhoneType').setValidators(Validators.compose([Validators.required]));
      this.tppForm.get('tin').setValidators(Validators.compose([Validators.required, Validators.maxLength(9), Validators.minLength(9), Validators.pattern("^[0-9]+$")]));
      this.tppForm.get('emailAddress').setValidators(Validators.compose([Validators.required, Validators.maxLength(255), Validators.pattern("[a-zA-Z0-9_\\.-]+@([a-zA-Z0-9-]+\\.)+[a-zA-Z]{2,6}$")]));
      this.tppForm.get('otherNames').setValidators(Validators.compose([Validators.maxLength(50), Validators.minLength(2), Validators.pattern("^[a-zA-Z0-9 .\\[\\]/#&',()-]+$")]));
      this.tppForm.get('companyHealthType').setValidators(Validators.compose([Validators.required]));

      this.resetFormArray();
    }

    if ($event.target.value === 'Agent') {
      this.showDivOptSectionB = true;
      this.showDivOptSectionA = false;
      this.showDivOtherHealthType = false;

      this.tppForm.get('insurerOrSelfInsuredName').reset();
      this.tppForm.get('insurerOrSelfInsuredName').clearValidators();
      this.tppForm.get('insurerOrSelfInsuredName').updateValueAndValidity();
      this.tppForm.get('contactName').reset();
      this.tppForm.get('street1').reset();
      this.tppForm.get('street2').reset();
      this.tppForm.get('city').reset();
      this.tppForm.get('state').reset();
      this.tppForm.get('zip').reset();
      this.tppForm.get('primaryPhone').reset();
      this.tppForm.get('primaryPhoneType').reset();
      this.tppForm.get('tin').reset();
      this.tppForm.get('tin').clearValidators();
      this.tppForm.get('tin').updateValueAndValidity();
      this.tppForm.get('emailAddress').reset();
      this.tppForm.get('otherNames').reset();
      this.tppForm.get('otherNames').clearValidators();
      this.tppForm.get('otherNames').updateValueAndValidity();
      this.tppForm.get('companyHealthType').reset();
      this.tppForm.get('companyHealthType').clearValidators();
      this.tppForm.get('companyHealthType').updateValueAndValidity();
      this.tppForm.get('otherCompanyHealthType').clearValidators();
      this.tppForm.get('otherCompanyHealthType').updateValueAndValidity();
      this.tppForm.get('otherCompanyHealthType').setValue('');

      this.tppForm.get('filerType')?.setValue($event.target.value);
      this.tppForm.get('agentsCompanyName').setValidators(Validators.compose([Validators.required, Validators.maxLength(50), Validators.minLength(2), Validators.pattern("^[a-zA-Z0-9 .\\[\\]/#&'()-]+$")]));
      this.tppForm.get('contactName').setValidators(Validators.compose([Validators.maxLength(50), Validators.minLength(2), Validators.pattern("^[a-zA-Z0-9 .\\[\\]/#&'()-]+$")]));
      this.tppForm.get('street1').setValidators(Validators.compose([Validators.required, Validators.maxLength(50), Validators.minLength(2), Validators.pattern("^[a-zA-Z0-9 .\\[\\]/#&'()-]+$")]));
      this.tppForm.get('street2').setValidators(Validators.compose([Validators.maxLength(50), Validators.minLength(2), Validators.pattern("^[a-zA-Z0-9 .\\[\\]/#&'()-]+$")]));
      this.tppForm.get('city').setValidators(Validators.compose([Validators.required, Validators.maxLength(40), Validators.minLength(2), Validators.pattern("^[a-zA-Z0-9 .\\[\\]/#&'()-]+$")]));
      this.tppForm.get('state').setValidators(Validators.compose([Validators.required]));
      this.tppForm.get('zip').setValidators(Validators.compose([Validators.required, Validators.pattern("^[0-9]{5}(?:-[0-9]{4})?$")]));
      this.tppForm.get('primaryPhone').setValidators(Validators.compose([Validators.required, Validators.maxLength(10), Validators.minLength(10), Validators.pattern("^[0-9]+$")]));
      this.tppForm.get('primaryPhoneType').setValidators(Validators.compose([Validators.required]));
      this.tppForm.get('agentsTin').setValidators(Validators.compose([Validators.required, Validators.maxLength(9), Validators.minLength(9), Validators.pattern("^[0-9]+$")]));
      this.tppForm.get('emailAddress').setValidators(Validators.compose([Validators.required, Validators.maxLength(255), Validators.pattern("[a-zA-Z0-9_\\.-]+@([a-zA-Z0-9-]+\\.)+[a-zA-Z]{2,6}$")]));
      this.tppForm.get('relationshipType').setValidators(Validators.compose([Validators.required]));

      this.resetFormArray();
    }
  }

  getCompanyHealthTypesValue($event) {
    // if you need the event source and value it can be accessed.
    console.log($event.target.value);
    if ($event.target.value === 'Other') {
      this.showDivOtherHealthType = true;
      this.tppForm.get('otherCompanyHealthType').setValidators(Validators.compose([Validators.required, Validators.maxLength(50), Validators.minLength(2), Validators.pattern("^[a-zA-Z0-9 .\\[\\]/#&'()-]+$")]));
    }

    if ($event.target.value !== 'Other') {
      this.showDivOtherHealthType = false;
      this.tppForm.get('otherCompanyHealthType').clearValidators();
      this.tppForm.get('otherCompanyHealthType').updateValueAndValidity();
      this.tppForm.get('otherCompanyHealthType').setValue('');
    }
  }

  getRelationshipTypesValue($event) {
    // if you need the event source and value it can be accessed.
    console.log($event.target.value);
    if ($event.target.value === 'Other') {
      this.showDivOtherRelationshipType = true;
      this.tppForm.get('otherRelationshipType').setValidators(Validators.compose([Validators.required, Validators.maxLength(50), Validators.minLength(2), Validators.pattern("^[a-zA-Z0-9 .\\[\\]/#&'()-]+$")]));
    }

    if ($event.target.value !== 'Other') {
      this.showDivOtherRelationshipType = false;
      this.tppForm.get('otherRelationshipType').clearValidators();
      this.tppForm.get('otherRelationshipType').updateValueAndValidity();
      this.tppForm.get('otherRelationshipType').setValue('');
    }
  }

  getFileDetails(e) {
    for (let i = 0; i < e.target.files.length; i++) {
      this.supportingDocuments.push(e.target.files[i]);
      /*console.log(e.target.files[i]);*/
    }
  }

  removeRow(i: number) {
    this.supportingDocuments.splice(i, 1)
    this.fileInput.nativeElement.value = null;
  }

  // Clear ClaimDetails Form Array Controls
  resetFormArray() {
    this.classMembers().clear();
  }

  // Remove the second element and so on
  removeAllExceptFirst(formArray: FormArray): void {
    while (formArray.length > 1) {
      formArray.removeAt(0);
    }
  }

  removeTable() {
    if (this.tableRef) {
      this.tableRef.nativeElement.remove();
    }
  }

  // Declare FormArray Function
  classMembers(): FormArray {
    return this.tppForm.get('classMembers') as FormArray
  }

  // Create new Form Array
  newClassMember(): FormGroup {
    return this.fb.group({
      classMemberName: ['', Validators.compose([Validators.required, Validators.maxLength(50), Validators.minLength(2), Validators.pattern("^[a-zA-Z0-9 .\\[\\]/#&'()-]+$")])],
      classMemberFEIN: ['', Validators.compose([Validators.required, Validators.maxLength(9), Validators.minLength(9), Validators.pattern("^[0-9]+$")])],
    });
  }

  // Create a New Array Function
  classMemberARR(index) {
    this.cmList = this.tppForm.get('classMembers') as FormArray;
    const formGroup = this.cmList.controls[index] as FormGroup;
    return formGroup;
  }

  addData() {
    (<FormArray>this.tppForm.get('classMembers')).push(this.newClassMember());
  }

  removeData(i: number) {
    this.classMembers().removeAt(i)
  }

  // Create new Form Array
  newClassMemberPA(): FormGroup {
    return this.fb.group({
      classMemberName: ['', Validators.compose([Validators.required, Validators.maxLength(50), Validators.minLength(2), Validators.pattern("^[a-zA-Z0-9 .\\[\\]/#&'()-]+$")])],
      classMemberPaidAmount: ['', Validators.compose([Validators.required, Validators.maxLength(15), Validators.pattern(/^\d{0,15}(?:[.]\d{1,4})?$/)])],
    });
  }

  onSubmit() {
    this.submitted = true;
    this.dateLoaded = new Date();
    this.claimEndTime = this.dateLoaded.toLocaleTimeString();
    // stop here if form is invalid
    if (this.tppForm.invalid) {
      this.showDivSubmittalErrorMessage = true;
      return;
    }


    const control1 = this.tppForm.get('sandoxSettlementClass');
    const control2 = this.tppForm.get('apotexSettlementClass');
    const control3 = this.tppForm.get('heritageSettlementClass');
    const control4 = this.tppForm.get('sunTaroSettlementClass');

    if (!control1?.touched) {
      control1?.setValue(false);
    }
    if (!control2?.touched) {
      control2?.setValue(false);
    }
    if (!control3?.touched) {
      control3?.setValue(false);
    }
    if (!control4?.touched) {
      control4?.setValue(false);
    }

    this.clicked = true;
    const claimant = this.tppForm.value;
    claimant.claimStartTime = this.claimStartTime;
    claimant.claimEndTime = this.claimEndTime;
    this.spinner.show();

    const formData = new FormData();

    // MUST stringify
    const claimantJson = JSON.stringify(claimant);

    // MUST wrap in Blob
    const claimantBlob = new Blob(
      [claimantJson],
      { type: 'application/json' }
    );

    // MUST include the SAME name the API expects
    formData.append(
      'Claimant.Json',
      JSON.stringify(claimant)
    );

    // Files
    this.supportingDocuments.forEach(file => {
      formData.append('Files', file, file.name);
    });

    // Single API call
    this.tppClaimantService.createClaimant(formData).subscribe({
      next: (res) => {
        this.storage.store('claimant', res);
        this.tppForm.reset();
        this.spinner.hide();
        this.router.navigate(['/tpp-complete']);
      },
      error: err => {
        this.spinner.hide();
        console.group('Claimant + document submission failed');
        console.log('Status:', err.status);
        console.log('Error:', err.error);
        console.groupEnd();
      }
    });
  }  
}
